import { z } from "zod";

/** Only URLs actually observed in the card are passed here; never synthesize a detail URL. */
export function safeProductUrl(value: string | undefined, base?: string): string | undefined {
  if (!value?.trim()) return undefined;
  try {
    const url = new URL(value, base), host = url.hostname.toLowerCase();
    if (!/^https?:$/.test(url.protocol) || url.username || url.password || url.port || !host.includes(".") || host.endsWith(".")
      || host.includes(":") || /^[\d.]+$/.test(host) || /(?:^|\.)(?:localhost|local|internal|lan|home|test|invalid)$/.test(host)) return undefined;
    if (/(?:^|\/)(?:login|signin|sign-in|auth|account|logout|checkout|payment|delete|track|tracking|pixel|redirect|click|sspa)(?:[/.?_-]|$)/i.test(url.pathname)) return undefined;
    if ([...url.searchParams.keys()].some(key => /token|secret|password|passwd|api.?key|authorization|session|email|phone|signature|credential|^code$|^sid$|^redirect|^url$/i.test(key))) return undefined;
    for (const key of [...url.searchParams.keys()]) if (/^(?:utm_|ref|tag$|linkCode$|camp$|creative$|qid$|sr$|spm$|gclid$|fbclid$)/i.test(key)) url.searchParams.delete(key);
    url.hash = "";
    return url.href.length <= 2048 ? url.href : undefined;
  } catch { return undefined; }
}
const publicUrl = z.string().max(2048).refine(value => safeProductUrl(value) === value, "Only canonical public product URLs are allowed");
/** 整页商品上限。单一所有者：schema 与下面的逐条校验都必须用这个常量。 */
export const MAX_PAGE_PRODUCTS = 24;
export const MAX_PRODUCT_CARDS = 12;
export const PageProductSchema = z.object({
  id: z.string().regex(/^product-[a-f0-9]{16}$/), sourceUrl: publicUrl,
  /** Observed card identity only; never a fabricated detail URL. */
  productKey: z.string().regex(/^temu\.com:[1-9]\d{0,19}$/).optional(),
  title: z.string().min(2).max(300), url: publicUrl.optional(), imageUrl: publicUrl.optional(),
  price: z.string().max(100).optional(), rating: z.number().min(0).max(5).optional(),
  /**
   * 展示价的 ISO 4217 币种，**只在页面标记无歧义时**才给。
   * `$`（USD/CAD/AUD/SGD/HKD/MXN…）与 `¥`（CNY/JPY）一律留空：从标记反推币种属于臆造，
   * 那种情况由会话目标里的目标币种承担口径。
   */
  currency: z.string().trim().length(3).optional(),
  reviewCount: z.number().int().nonnegative().optional(), purchaseText: z.string().max(200).optional(),
  purchaseCount: z.number().int().nonnegative().optional(), purchaseLowerBound: z.boolean().optional(),
  purchasePeriod: z.enum(["month", "total", "unknown"]).optional(),
}).strict();
export type PageProduct = z.infer<typeof PageProductSchema>;
export function productId(product: Pick<PageProduct, "sourceUrl" | "title" | "url" | "imageUrl" | "productKey">): string {
  const value = JSON.stringify([product.sourceUrl, product.title, product.url || "", product.imageUrl || "", ...(product.productKey ? [product.productKey] : [])]);
  let a = 2166136261, b = 5381;
  for (let i = 0; i < value.length; i++) { a = Math.imul(a ^ value.charCodeAt(i), 16777619); b = Math.imul(b, 33) ^ value.charCodeAt(i); }
  return `product-${(a >>> 0).toString(16).padStart(8,"0")}${(b >>> 0).toString(16).padStart(8,"0")}`;
}
/**
 * 逐商品校验：**单个商品不合格式只丢它自己**，不让整页读取或整条请求失败。
 *
 * 以前调用方是整对象 `PageContextSchema.parse`，任一 `products[i].id` 不合法就抛错，
 * 连正文一起丢掉——用户看到的是"这一页读不到"，而不是"其中一个商品有问题"。
 *
 * 刻意**不做**一致性/业务校验（sourceUrl 是否等于页面 URL、id 是否等于重算值等）：
 * 那些是服务端 `validatedPageProducts` 的职责，混进来会让页面侧行为跟着变。
 * 返回的 `dropped` 必须由调用方显式处理，**不允许静默丢弃**。
 */
export function parseProductsIsolated(value: unknown, limit = MAX_PAGE_PRODUCTS): { products: PageProduct[]; dropped: number } {
  const raw = Array.isArray(value) ? value : [];
  const products: PageProduct[] = [];
  let dropped = Math.max(0, raw.length - limit);
  for (const item of raw.slice(0, limit)) {
    const parsed = PageProductSchema.safeParse(item);
    if (!parsed.success) { dropped += 1; continue; }
    products.push(parsed.data);
  }
  return { products, dropped };
}
/**
 * 仅当失败**可归因于商品数组**时，返回"删掉坏商品"的克隆；否则返回 undefined。
 *
 * 供 `parsePageContextIsolated` 与 zod `.preprocess()` 复用同一套判定——
 * 判定只放一处，避免"页面侧隔离、服务端不隔离"这种漂移。
 */
export function isolateProductFailures(input: unknown, limit = MAX_PAGE_PRODUCTS): { context: Record<string, unknown>; dropped: number } | undefined {
  if (!input || typeof input !== "object" || Array.isArray(input)) return undefined;
  const record = input as Record<string, unknown>;
  if (!Array.isArray(record.products) || record.products.length === 0) return undefined;
  const { products, dropped } = parseProductsIsolated(record.products, limit);
  if (dropped === 0) return undefined; // 商品没问题 → 是别的字段坏了，交回调用方整体失败
  return { context: { ...record, products }, dropped };
}
/** 只做商品级隔离的"已读取商品"卡片解析；label/note 不合格式仍然整体拒绝。 */
export function parseProductCardsIsolated(value: unknown): { cards: ProductCards; dropped: number } | undefined {
  const direct = ProductCardsSchema.safeParse(value);
  if (direct.success) return { cards: direct.data, dropped: 0 };
  const repaired = isolateProductFailures(value, MAX_PRODUCT_CARDS);
  if (!repaired) return undefined;
  const retry = ProductCardsSchema.safeParse(repaired.context);
  return retry.success ? { cards: retry.data, dropped: repaired.dropped } : undefined;
}
export const ProductCardsSchema = z.object({ label: z.literal("本次已读取商品"), products: z.array(PageProductSchema).max(MAX_PRODUCT_CARDS), note: z.string().max(1600) }).strict();
export type ProductCards = z.infer<typeof ProductCardsSchema>;
